import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { fetchAuctionCompanies, loginUser, clearError } from '../../../redux/slices/authSlice';
import { RootState, AppDispatch } from '../../../redux/store';
import ASSET_PATHS from '../../../assets/config/assetPaths';
import { AiOutlineEye, AiOutlineEyeInvisible } from 'react-icons/ai';
import { ImSpinner2 } from 'react-icons/im';
import Dropdown, { DropdownOption } from '../../../components/template/Drowpdown/dropdown';
import { fetchInspectionTemplates } from '../../../redux/slices/inspectionTemplatesSlice';
import { fetchClarityId, clearClarity } from '../../../redux/slices/claritySlice';
import { loadClarity } from '../../../utils/clarity';


const Login: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();

    const [email, setEmail] = useState<string>('');
    const [password, setPassword] = useState<string>('');
    const [auctionCompany, setAuctionCompany] = useState<{ tenantKey: string, tenantName: string }>();
    const [showPassword, setShowPassword] = useState<boolean>(false);
    const [step, setStep] = useState<number>(1);

    const [emailError, setEmailError] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [passwordError, setPasswordError] = useState<string | null>(null);
    const [loginError, setLoginError] = useState<string | null>(null);

    const { auctionCompanies } = useSelector((state: RootState) => state.auth);
    const { clarityId, status: clarityStatus } = useSelector((state: RootState) => state.clarity);

    useEffect(() => {
        dispatch(clearError());
        dispatch(clearClarity());
    }, [dispatch, step]);

    useEffect(() => {
        if (auctionCompanies.length > 0) {
            setAuctionCompany({tenantKey: auctionCompanies[0].value, tenantName: auctionCompanies[0].label});
        }
    }, [auctionCompanies]);

    useEffect(() => {
        if (step === 2 && auctionCompany) {
        dispatch(fetchClarityId(auctionCompany.tenantKey));
        }
    }, [step, auctionCompany, dispatch]);

    useEffect(() => {
        if (clarityStatus === 'succeeded' && clarityId) {
        loadClarity(clarityId);
        }
    }, [clarityId]);

    const validateEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

    const handleEmailSubmit = async (e: React.FormEvent) => {
        try {
            e.preventDefault();

            if (!email) {
                setEmailError('Email is required');
                return;
            }
            if (!validateEmail(email)) {
                setEmailError('Invalid email address');
                return;
            }
            setEmailError(null);

            setIsLoading(true);
            const resultAction = await dispatch(fetchAuctionCompanies(email));
            if (fetchAuctionCompanies.fulfilled.match(resultAction)) {
                if (resultAction.payload?.length === 0) {
                    setEmailError('Couldn’t find your email. Enter the correct email.');
                } else {
                    setStep(2);
                }
            }
        } catch (err: any) {
        } finally {
            setIsLoading(false);
        }
    };

    const handleLogin = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!password) {
            setPasswordError('Password is required');
            return;
        }
        setPasswordError(null);

        try {
            setIsLoading(true)
            const { token } = await dispatch(loginUser({ email, password, auctionCompany: auctionCompany as { tenantKey: string; tenantName: string } })).unwrap();
            if (token)
                await dispatch(fetchInspectionTemplates({ token }));
            navigate('/dashboard');
        } catch (err: any) {
            console.log('Login failed:', err);
            setLoginError(err);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSelectAuctionCompany = (option: DropdownOption) => setAuctionCompany({tenantKey: option.value, tenantName: option.label});

    return (
        <div className="flex justify-center min-h-screen bg-gray-100 text-textPrimary dark:bg-backgroundDark dark:text-textPrimaryDark">
            <div className="w-full max-w-sm p-8">
                <div className="flex justify-center mb-16 mt-14">
                    <img src={ASSET_PATHS.logo} alt="Innova Technologies Logo" className="w-50" />
                </div>

                {step === 1 && (
                    <form onSubmit={handleEmailSubmit} noValidate>
                        <div className="mb-5">
                            <label htmlFor="email" className="block text-sm font-medium mb-1">
                                Email Address
                            </label>
                            <input
                                id="email"
                                name="email"
                                type="email"
                                value={email}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    setEmail(value);
                                    if (emailError && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
                                        setEmailError(null);
                                    }
                                }}
                                required
                                className={`w-full px-4 py-2 border ${emailError ? 'border-red-500 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-primary focus:border-primary'
                                    } rounded-md focus:outline-none dark:border-gray-700 dark:bg-gray-700`}
                                placeholder="Enter your email"
                            />


                            {emailError && <p className="text-red-500 text-xs mt-1">{emailError}</p>}
                        </div>
                        <div className="mt-12">
                            <button
                                type="submit"
                                className="w-full px-4 py-2 text-white bg-primary rounded-md hover:bg-primary focus:outline-none focus:ring-2 focus:ring-primary dark:bg-primary dark:hover:bg-primary flex items-center justify-center shadow-button-shadow"
                                disabled={isLoading}
                            >
                                {isLoading ? (
                                    <div className="py-1">
                                        <ImSpinner2 className="animate-spin mr-2" />
                                    </div>
                                ) : (
                                    'Next'
                                )}
                            </button>
                        </div>
                    </form>
                )}

                {step === 2 && (
                    <form onSubmit={handleLogin} noValidate>
                        <div className="mb-5">
                            <label htmlFor="email" className="block text-sm font-medium mb-1">
                                Email Address
                            </label>
                            <input
                                id="email"
                                name="email"
                                type="email"
                                value={email}
                                readOnly
                                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary dark:border-gray-700 dark:bg-gray-700"
                            />
                        </div>

                        <div className="mb-5">
                            <label htmlFor="auctionCompany" className="block text-sm font-medium mb-1">
                                Company
                            </label>
                            <Dropdown
                                options={auctionCompanies}
                                isSearchable={false}
                                placeholder="Select"
                                onSelect={handleSelectAuctionCompany}
                                value={auctionCompany?.tenantKey}
                            />
                        </div>

                        <div className="mb-6">
                            <label htmlFor="password" className="block text-sm font-medium mb-1">
                                Password
                            </label>
                            <div className="relative">
                                <input
                                    id="password"
                                    name="password"
                                    type={showPassword ? 'text' : 'password'}
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required
                                    className={`w-full px-4 py-2 border ${passwordError || loginError ? 'border-red-500 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-primary focus:border-primary'
                                        } rounded-md focus:outline-none dark:border-gray-700 dark:bg-gray-700`}
                                    placeholder="Enter your password"
                                />
                                <button
                                    type="button"
                                    className="absolute inset-y-0 right-0 px-3 flex items-center justify-center h-full"
                                    onClick={() => setShowPassword(!showPassword)}
                                >
                                    {showPassword ? <AiOutlineEyeInvisible className="text-xl" /> : <AiOutlineEye className="text-xl" />}
                                </button>
                            </div>
                            {passwordError && <p className="text-red-500 text-xs mt-1">{passwordError}</p>}
                            {loginError && <div className="text-red-500 text-xs mt-2">{loginError}</div>}
                        </div>

                        <div className="mt-12">
                            <button
                                type="submit"
                                className="w-full px-4 py-2 text-white bg-primary rounded-md hover:bg-primary focus:outline-none focus:ring-2 focus:ring-primary dark:bg-primary dark:hover:bg-primary flex items-center justify-center shadow-button-shadow"
                                disabled={isLoading}
                            >
                                {isLoading ? (
                                    <div className="py-1">
                                        <ImSpinner2 className="animate-spin mr-2" />
                                    </div>
                                ) : (
                                    'Login'
                                )}
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
};

export default Login;
